import { z } from 'zod';

// The bank's shapes, shared by the server and the browser (design §10.1, plan 2A
// Assumptions 1 and 2). Amounts are whole aUEC as JSON numbers, well inside 2^53.
// Instants on the wire are ISO strings in UTC.

/** A record id, as PocketBase makes them. */
export const RecordId = z.string().regex(/^[a-z0-9]{15}$/, 'must be a record id');

/** The largest single amount: a trillion aUEC. */
export const MAX_AMOUNT = 1e12;

/** One entry's amount: whole aUEC, from 1 to 10^12. */
export const Amount = z.number().int().min(1).max(MAX_AMOUNT);

/** The only currency, recorded on every entry so another could be added later (D16). */
export const CURRENCY = 'aUEC';

/** The far side of an entry that money enters or leaves the bank by. */
export const EXTERNAL = 'external';

export const AccountKind = z.enum(['primary', 'held']);
export type AccountKind = z.infer<typeof AccountKind>;

export const Account = z.object({
  id: z.string(),
  name: z.string(),
  kind: AccountKind,
  /** User ids. */
  holders: z.array(z.string()),
  active: z.boolean(),
  notes: z.string(),
  /** The current period's balance. */
  balance: z.number().int(),
  version: z.number().int(),
  created: z.iso.datetime(),
});
export type Account = z.infer<typeof Account>;

export const EntryKind = z.enum(['deposit', 'withdrawal', 'payout', 'transfer', 'adjustment', 'period_close']);
export type EntryKind = z.infer<typeof EntryKind>;

/** `awaiting_receipt` is a transfer waiting for a holder of the receiving account. */
export const EntryStatus = z.enum(['pending', 'awaiting_receipt', 'completed', 'rejected', 'cancelled']);
export type EntryStatus = z.infer<typeof EntryStatus>;

/** What an entry refers to. */
export const Reference = z.object({ type: z.enum(['loan', 'reconciliation', 'entry']), id: RecordId });
export type Reference = z.infer<typeof Reference>;

/** Where an entry came from when another module recorded it through a call (plan 2C, ADR 0013): the module and the call's key. */
export const Origin = z.object({ module: z.string(), key: z.string() });
export type Origin = z.infer<typeof Origin>;

/** Who gave a deposit: an org member by user id, or someone from outside by name. */
export const Donor = z.object({ userId: z.string().nullable(), name: z.string().nullable() });
export type Donor = z.infer<typeof Donor>;

export const Entry = z.object({
  id: z.string(),
  kind: EntryKind,
  status: EntryStatus,
  period: z.number().int(),
  /** An account id, or `external`. */
  source: z.string(),
  destination: z.string(),
  /** Always positive; `source` and `destination` give the direction. */
  amount: z.number().int(),
  currency: z.literal(CURRENCY),
  payee: z.string().nullable(),
  donor: Donor.nullable(),
  reference: Reference.nullable(),
  reverses: z.string().nullable(),
  /** Null for an entry recorded in the bank itself. */
  origin: Origin.nullable(),
  memo: z.string(),
  dispute: z.string(),
  recordedBy: z.string(),
  /** The approver or rejecter, or whoever cancelled it. */
  decidedBy: z.string().nullable(),
  decidedAt: z.iso.datetime().nullable(),
  /** The receiving holder who confirmed a transfer, or the payee who confirmed a payout. */
  confirmedBy: z.string().nullable(),
  confirmedAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
  created: z.iso.datetime(),
  version: z.number().int(),
});
export type Entry = z.infer<typeof Entry>;

export const Period = z.object({
  number: z.number().int(),
  startedAt: z.iso.datetime(),
  closedAt: z.iso.datetime().nullable(),
});
export type Period = z.infer<typeof Period>;

/** Display names by user id, for the people a response mentions. */
export const People = z.record(z.string(), z.string());
export type People = z.infer<typeof People>;

export const Summary = z.object({
  period: Period,
  /** The sum of the active accounts' balances. */
  total: z.number().int(),
  /** Active accounts: the primary first, then by balance. */
  accounts: z.array(Account),
  people: People,
  /** Accounts whose holders have all lost access (plan 2A Assumption 9). */
  holderLeft: z.array(z.string()),
});
export type Summary = z.infer<typeof Summary>;

// Inputs.

const Holders = z
  .array(RecordId)
  .max(20)
  .default([])
  .transform((ids) => [...new Set(ids)]);

export const AccountInput = z.object({
  name: z.string().trim().min(1).max(64),
  kind: AccountKind,
  holders: Holders,
  notes: z.string().trim().max(1000).default(''),
});
export type AccountInput = z.input<typeof AccountInput>;

export const AccountUpdate = AccountInput.extend({ version: z.number().int().min(1) });
export type AccountUpdate = z.input<typeof AccountUpdate>;

const Memo = z.string().trim().max(500).default('');

/** A deposit's donor as entered: a member, or a name for someone outside the org. */
export const DonorInput = z.union([z.object({ userId: RecordId }).strict(), z.object({ name: z.string().trim().min(1).max(100) }).strict()]);
export type DonorInput = z.infer<typeof DonorInput>;

export const DepositInput = z.object({
  kind: z.literal('deposit'),
  account: RecordId,
  amount: Amount,
  memo: Memo,
  donor: DonorInput.nullable().default(null),
});

export const WithdrawalInput = z.object({
  kind: z.literal('withdrawal'),
  account: RecordId,
  amount: Amount,
  memo: Memo,
});

/** Pays a member out of an account. */
export const PayoutInput = z.object({
  kind: z.literal('payout'),
  account: RecordId,
  amount: Amount,
  payee: RecordId,
  memo: Memo,
});

/** Moves money between two accounts; a holder of the receiving one confirms it arrived. */
export const TransferInput = z
  .object({
    kind: z.literal('transfer'),
    from: RecordId,
    to: RecordId,
    amount: Amount,
    memo: Memo,
  })
  .refine((input) => input.from !== input.to, { message: 'Pick two different accounts', path: ['to'] });

/** Corrects an account's balance by a signed amount: `bank.reconcile` only. */
export const AdjustmentInput = z.object({
  kind: z.literal('adjustment'),
  account: RecordId,
  delta: z
    .number()
    .int()
    .min(-MAX_AMOUNT)
    .max(MAX_AMOUNT)
    .refine((delta) => delta !== 0, 'An adjustment changes the balance'),
  memo: Memo,
});

/** What recording an entry takes, by kind. */
export const EntryInput = z.discriminatedUnion('kind', [DepositInput, WithdrawalInput, PayoutInput, TransferInput, AdjustmentInput]);
export type EntryInput = z.input<typeof EntryInput>;

/** What the caller may do to an entry now, so pages show only the buttons that will work. The server checks again. */
export const EntryActions = z.object({
  approve: z.boolean(),
  reject: z.boolean(),
  cancel: z.boolean(),
  confirm: z.boolean(),
  dispute: z.boolean(),
  reverse: z.boolean(),
});
export type EntryActions = z.infer<typeof EntryActions>;

/** Someone a picker offers. */
export const Person = z.object({ id: z.string(), displayName: z.string() });
export type Person = z.infer<typeof Person>;


/** A user's limits: null is no limit (`bank.reconcile` and Administrators). */
export const Limits = z.object({ recording: z.number().int().nullable(), approval: z.number().int().nullable() });
export type Limits = z.infer<typeof Limits>;

/** A group's limits as the admin section edits them. `version` is null until they're first set. */
export const GroupLimits = z.object({
  group: z.object({ id: z.string(), name: z.string() }),
  recordingLimit: z.number().int(),
  approvalLimit: z.number().int(),
  version: z.number().int().nullable(),
});
export type GroupLimits = z.infer<typeof GroupLimits>;

export const GroupLimitsInput = z.object({
  recordingLimit: z.number().int().min(0).max(MAX_AMOUNT),
  approvalLimit: z.number().int().min(0).max(MAX_AMOUNT),
  /** The version read, or null to set a group's limits for the first time. */
  version: z.number().int().min(1).nullable(),
});
export type GroupLimitsInput = z.input<typeof GroupLimitsInput>;

// Calls other modules make (public.ts, ADR 0013).

/** `recordDeposit`: a deposit into an active account, recorded by the call's `for` user, who must hold it. */
export const RecordDepositInput = z.object({
  account: RecordId,
  amount: Amount,
  memo: Memo,
  donor: DonorInput.nullable().default(null),
});
export type RecordDepositInput = z.input<typeof RecordDepositInput>;

export const RecordDepositResult = z.object({ entryId: z.string(), account: z.string(), amount: z.number().int() });
export type RecordDepositResult = z.infer<typeof RecordDepositResult>;

/**
 * `recordPayout`: a payout from an account to a member, recorded by the call's `for` user, who must be
 * allowed to record entries. It is recorded as one made on the bank's page is: it completes at once only
 * within that user's recording limit, and otherwise waits for an approver. The payee can confirm or
 * dispute it once it has completed.
 */
export const RecordPayoutInput = z.object({
  account: RecordId,
  amount: Amount,
  /** The member paid, by user id. */
  payee: RecordId,
  memo: Memo,
});
export type RecordPayoutInput = z.input<typeof RecordPayoutInput>;

/** `status` is `completed` if it moved money at once and `pending` if it waits for an approver. */
export const RecordPayoutResult = z.object({ entryId: z.string(), status: EntryStatus, account: z.string(), amount: z.number().int() });
export type RecordPayoutResult = z.infer<typeof RecordPayoutResult>;

/** `reverseDeposit`: reverses the deposit the caller recorded with `key`, under the bank's reversal rules. */
export const ReverseDepositInput = z.object({ key: z.string().min(1).max(128), memo: Memo });
export type ReverseDepositInput = z.input<typeof ReverseDepositInput>;

export const ReverseDepositResult = z.object({ entryId: z.string(), reverses: z.string(), amount: z.number().int() });
export type ReverseDepositResult = z.infer<typeof ReverseDepositResult>;

/** An active account as other modules see it: no balance. */
export const CallAccount = z.object({ id: z.string(), name: z.string(), kind: AccountKind, holders: z.array(z.string()) });
export type CallAccount = z.infer<typeof CallAccount>;

/**
 * `payoutStatuses`: how the payouts the caller recorded stand, by the keys it recorded them with. Only the caller's own
 * entries are found, and a key that matches none is left out. `confirmed` is the payee's confirmation and `disputed` their
 * dispute, neither of which is an ending (plan 5D, R6).
 */
export const PayoutStatusesInput = z.object({ keys: z.array(z.string().min(1).max(128)).min(1).max(100) });
export type PayoutStatusesInput = z.input<typeof PayoutStatusesInput>;

export const PayoutStatusesResult = z.object({
  payouts: z.array(z.object({ key: z.string(), entryId: z.string(), status: EntryStatus, confirmed: z.boolean(), disputed: z.boolean() })),
});
export type PayoutStatusesResult = z.infer<typeof PayoutStatusesResult>;

/** `accounts`: the active accounts, the primary first, then by name. */
export const AccountsResult = z.object({ accounts: z.array(CallAccount) });
export type AccountsResult = z.infer<typeof AccountsResult>;

/** `donorDeposits`: completed, unreversed deposits with a donor and no origin, per donor, since a time. An origin means another module recorded it, and that module counts it. */
export const DonorDepositsInput = z.object({ since: z.iso.datetime().nullable().default(null) });
export type DonorDepositsInput = z.input<typeof DonorDepositsInput>;

export const DonorDepositsResult = z.object({ donors: z.array(z.object({ userId: z.string().nullable(), name: z.string().nullable(), count: z.number().int(), amount: z.number().int(), last: z.iso.datetime() })) });
export type DonorDepositsResult = z.infer<typeof DonorDepositsResult>;
